import { FileUploader, Popup, TextBox } from 'devextreme-react';
import { Button } from 'devextreme-react/button';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { deleteApi, putApi } from '../../apiUtils';
import { useTranslation } from '../../contexts/translation';
// abych odlišil od nativního File
import { File as FileType } from '../../types/file.types';
import { Hyperlink } from '../../types/hyperlink.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { DataGridButton, IColumnType, IDataType, SisDataGrid, SisDataGridColumns } from '../table';
import './FileManager.scss';
import { HyperLinkForm } from './HyperLinkForm';

interface TabFilesProps {
  id: number;
  entityName: string;
  isReadonly: boolean;
  onFileCountChange?: any;
  uploadMode?: 'instantly' | 'useButtons' | 'useForm' | undefined;
  reference?: any;
  hideUploadButtons?: boolean;
  hideFileList?: boolean;
  isEdit?: boolean;
  tmpFiles?: any[];
  setTmpFiles?: any;
  onSubmit?: any;
  setOpenedTab?: any;
  mainEntity?: 'asset' | 'task';
  assetId?: number;
  assetName?: string;
}

export function TabFilesDatagrid(props: TabFilesProps) {
  const { translate } = useTranslation();
  const transFormName = 'File';
  const apiUrlListFilesMain = `${API_URL.FILE_LIST}?entity=${props.entityName}&entityId=${props.id}`;
  const apiUrlListHyperlinksMain = `${API_URL.HYPERLINK_LIST_FILTERED}?entity=${props.entityName}&entityId=${props.id}`;
  const apiUrlListFilesRelativeEntity =
    props.mainEntity === 'task'
      ? `${API_URL.FILE_LIST}?entity=Asset&entityId=${props.assetId}`
      : `${API_URL.FILE_LIST_BY_ASSET}?assetId=${props.id}`;
  const apiUrlListHyperlinksRelativeEntity =
    props.mainEntity === 'task'
      ? `${API_URL.HYPERLINK_LIST_FILTERED}?entity=Asset&entityId=${props.assetId}`
      : `${API_URL.HYPERLINK_LIST_BY_ASSET}?assetId=${props.id}`;

  // Načteme subory
  const { data: filesMain, mutate: mutateFilesMain } = useSWR<FileType[]>(
    props.entityName && props.id ? `${apiUrlListFilesMain}` : null,
    fetcher,
  );
  const { data: filesRelativeEntity, mutate: mutateFilesRelativeEntity } = useSWR<FileType[]>(
    props.entityName && props.id ? `${apiUrlListFilesRelativeEntity}` : null,
    fetcher,
  );
  // Načteme hyperlinks
  const { data: hyperlinksMain, mutate: mutateHyperlinksMain } = useSWR<Hyperlink[]>(
    props.entityName && props.id ? `${apiUrlListHyperlinksMain}` : null,
    fetcher,
  );
  const { data: hyperlinksRelativeEntity, mutate: mutateHyperlinksRelativeEntity } = useSWR<
    Hyperlink[]
  >(props.entityName && props.id ? `${apiUrlListHyperlinksRelativeEntity}` : null, fetcher);

  const handleFilesUpload = () => {
    mutateFilesMain();
    mutateFilesRelativeEntity();
    if (props.onFileCountChange) props.onFileCountChange();
  };

  const handleFileDelete = async (id: string) => {
    const idArray = id.split('_');
    const itemId = Number(idArray[1]);
    const entityType = idArray[0];
    if (
      !(await confirm(
        translate!(`Are you sure you want to delete ${entityType}?`, transFormName),
        translate!('Confirm', ''),
      ))
    )
      return;

    if (entityType === 'file') {
      await deleteApi({
        url: `${API_URL.FILE_DELETE_ITEM}?entity=${props.entityName}&entityId=${props.id}&id=${itemId}`,
        id: itemId,
        callAfterSuccess: () => {
          mutateFilesMain();
          mutateFilesRelativeEntity();
        },
      });
    }

    if (entityType === 'hyperlink') {
      await deleteApi({
        url: `${API_URL.HYPERLINK_DELETE}?id=${itemId}`,
        id: itemId,
        callAfterSuccess: () => {
          mutateHyperlinksMain();
          mutateHyperlinksRelativeEntity();
        },
      });
    }
  };

  const [editPopupVisible, setEditPopupVisible] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editName, setEditName] = useState('');
  const [editUri, setEditUri] = useState('');

  const handleEdit = (rowData: any) => {
    setEditItem(rowData);
    setEditDescription(rowData.description || '');
    setEditName(rowData.name || '');
    setEditUri(rowData.url || '');
    setEditPopupVisible(true);
  };

  const handleEditClose = () => {
    setEditPopupVisible(false);
    setEditItem(null);
  };

  const handleEditSave = async () => {
    if (!editItem) return;
    const idArray = editItem.id.split('_');
    const entityId = Number(idArray[1]);
    const entityType = idArray[0];

    if (entityType === 'file') {
      await putApi({
        url: `${API_URL.FILE_PUT}?entity=${props.entityName}&entityId=${props.id}`,
        data: { id: entityId, description: editDescription },
        callAfterSuccess: () => {
          mutateFilesMain();
          mutateFilesRelativeEntity();
          handleEditClose();
        },
      });
    }

    if (entityType === 'hyperlink') {
      await putApi({
        url: `${API_URL.HYPERLINK_UPDATE}`,
        data: {
          id: entityId,
          entity: props.entityName,
          entityId: props.id,
          name: editName,
          uri: editUri,
        },
        callAfterSuccess: () => {
          mutateHyperlinksMain();
          mutateHyperlinksRelativeEntity();
          handleEditClose();
        },
      });
    }
  };

  const handlePreview = async (rowData: any) => {
    const idArray = rowData.id.split('_');
    const entityId = Number(idArray[1]);
    const entityType = idArray[0];

    if (entityType === 'hyperlink') {
      const raw: string = rowData.url;
      const href = raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
      window.open(href, '_blank');
      return;
    }

    const response = await fetch(
      `${API_URL.FILE_ITEM}?entity=${rowData.fileEntityName}&id=${entityId}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    );
    const blob = await response.blob();
    const fileUrl = URL.createObjectURL(blob);

    const previewable = /^(image\/|application\/pdf|text\/|video\/|audio\/)/.test(
      rowData.contentType,
    );
    if (previewable) {
      window.open(fileUrl, '_blank');
    } else {
      const anchor = document.createElement('a');
      anchor.href = fileUrl;
      anchor.download = rowData.name;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
  };

  const handleDownload = async (rowData: any) => {
    const idArray = rowData.id.split('_');
    const entityId = Number(idArray[1]);

    const response = await fetch(
      `${API_URL.FILE_ITEM}?entity=${rowData.fileEntityName}&id=${entityId}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    );
    const blob = await response.blob();
    const fileUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = rowData.name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const Columns = (handleEdit, translate): SisDataGridColumns => {
    const columns: any = [];

    columns.push({
      type: IColumnType.buttons,
      cellRender: (column: any) => {
        return (
          <>
            <DataGridButton
              hint={''}
              icon="find"
              cellData={column.data}
              onClick={() => handlePreview(column.data)}
            />
            {column.data.id.startsWith('file_') && (
              <DataGridButton
                hint={''}
                icon="download"
                cellData={column.data}
                onClick={() => handleDownload(column.data)}
              />
            )}
            {column.data.enableChange && (
              <>
                <DataGridButton
                  hint={''}
                  icon="edit"
                  cellData={column.data}
                  onClick={() => handleEdit(column.data)}
                />
                <DataGridButton
                  hint={''}
                  icon="trash"
                  cellData={column.data}
                  onClick={() => handleFileDelete(column.data.id)}
                />
              </>
            )}
          </>
        );
      },
      width: '8%',
    });

    if (props.mainEntity === 'task')
      columns.push({
        dataField: 'assetName',
        caption: 'Asset name',
        width: '15%',
        cellRender: (column: any) => {
          if (!column.data.assetName) return <></>;
          return <b>{column.data.assetName}</b>;
        },
      });

    if (props.mainEntity === 'asset')
      columns.push({
        dataField: 'taskCustomId',
        caption: 'Task id',
        width: '7%',
        cellRender: (column: any) => {
          if (!column.data.taskCustomId) return <></>;
          return (
            <Link
              to={`/tasks/edit/${column.data.taskId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <b>{column.data.taskCustomId}</b>
            </Link>
          );
        },
      });

    columns.push(
      {
        dataField: 'name',
        caption: 'Name',
        width: props.mainEntity === 'task' ? '15%' : '25%',
      },
      {
        dataField: 'description',
        caption: 'Description',
        width: props.mainEntity === 'task' ? '20%' : '25%',
      },
      {
        dataField: 'url',
        caption: 'URL',
        width: '15%',
        cellRender: (column: any) => {
          if (!column.data.url) return <></>;
          const raw: string = column.data.url;
          const href =
            raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
          return (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {raw}
            </a>
          );
        },
      },
      {
        dataType: IDataType.dateTime,
        dataField: 'created',
        caption: 'Created',
        width: '11%',
      },
      {
        dataField: 'createdBy',
        caption: 'Created by',
        width: '10%',
      },
      {
        dataType: IDataType.number,
        dataField: 'sizeInBytes',
        caption: 'Size',
        width: '5%',
        calculateCellValue: (row: any) =>
          row.sizeInBytes > 0 ? Math.round((row.sizeInBytes / 1024) * 10) / 10 : null,
      },
    );

    console.log('columns', columns);

    return columns;
  };

  const filesMerged = useMemo(() => {
    if (!filesMain || !hyperlinksMain || !filesRelativeEntity || !hyperlinksRelativeEntity)
      return [];
    return [
      ...filesMain.map((file) => ({
        id: 'file_' + file.id,
        assetName: '',
        taskCustomId: '',
        taskId: undefined,
        name: file.name,
        description: file.description,
        url: '',
        created: file.created,
        createdBy: file.createdByName,
        sizeInBytes: file.sizeInBytes,
        enableChange: true,
        contentType: file.contentType,
        fileEntityName: 'test',
      })),
      ...hyperlinksMain.map((link) => ({
        id: 'hyperlink_' + link.id,
        assetName: '',
        taskCustomId: '',
        taskId: undefined,
        name: link.name,
        description: '',
        url: link.uri,
        created: link.created,
        createdBy: link.createdByName,
        sizeInBytes: 0,
        enableChange: true,
        contentType: '',
        fileEntityName: '',
      })),
      ...filesRelativeEntity.map((file) => ({
        id: 'file_' + file.id,
        assetName: props.assetName ? props.assetName : '',
        taskCustomId: file.taskCustomId ? file.taskCustomId : '',
        taskId: file.taskId ? file.taskId : undefined,
        name: file.name,
        description: file.description,
        url: '',
        created: file.created,
        createdBy: file.createdByName,
        sizeInBytes: file.sizeInBytes,
        enableChange: false,
        contentType: file.contentType,
        fileEntityName: 'test',
      })),
      ...hyperlinksRelativeEntity.map((link) => ({
        id: 'hyperlink_' + link.id,
        assetName: props.assetName ? props.assetName : '',
        taskCustomId: link.taskCustomId ? link.taskCustomId : '',
        taskId: link.taskId ? link.taskId : undefined,
        name: link.name,
        description: '',
        url: link.uri,
        created: link.created,
        createdBy: link.createdByName,
        sizeInBytes: 0,
        enableChange: false,
        contentType: '',
        fileEntityName: '',
      })),
    ];
  }, [filesMain, filesRelativeEntity, hyperlinksMain, hyperlinksRelativeEntity]);

  console.log('files', filesMain);
  console.log('filesRelativeEntity', filesRelativeEntity);
  console.log('hyperlinks', hyperlinksMain);
  console.log('hyperlinksRelativeEntity', hyperlinksRelativeEntity);

  return (
    <div
      className={`file-manager responsive-paddings${
        props.hideUploadButtons ? ' hide-upload-buttons' : ''
      }`}
    >
      {!props.isReadonly && (
        <div style={{ display: 'flex', marginBottom: '20px' }}>
          <div>
            <div>
              <b>{translate!('File upload', transFormName)}</b>
            </div>
            <FileUploader
              name={'files'}
              multiple={true}
              accept={'*'}
              uploadMode={!props.uploadMode ? 'instantly' : props.uploadMode}
              selectButtonText={translate!('Select files', transFormName)}
              labelText={translate!('or drop files here', transFormName)}
              uploadUrl={`${API_URL.FILE_UPLOAD}?entity=${props.entityName}&entityId=${props.id}`}
              onUploaded={handleFilesUpload}
              onValueChange={(e: any) => {
                e.forEach((file) => {
                  if (file.size > 30000000)
                    notify(`${file.name} - ${translate!('File is too large', '')}`, 'error');
                });
                if (!e || !props.setTmpFiles) return;
                props.setTmpFiles(e);
              }}
              defaultValue={props.tmpFiles ? props.tmpFiles : []}
              showFileList={false}
              ref={props.reference || null}
              width={'400px'}
              maxFileSize={30000000}
            />
          </div>
          <div>
            <div>
              <b>{translate!('Hyperlink upload', transFormName)}</b>
            </div>
            <HyperLinkForm
              entity={props.entityName}
              entityId={props.id}
              onSuccess={() => {
                mutateHyperlinksMain();
                mutateHyperlinksRelativeEntity();
                if (props.onFileCountChange) props.onFileCountChange();
              }}
            />
          </div>
        </div>
      )}
      {!props.hideFileList && (
        <div>
          <SisDataGrid
            data={filesMerged}
            keyExpr={'id'}
            transFormName={transFormName}
            columns={Columns(handleEdit, translate)}
            columnChooser={false}
            allowColumnResizing={false}
            allowColumnReordering={false}
            enableGrouping
            pageSizeChangeVisible
            width={'100%'}
          />
        </div>
      )}
      {editPopupVisible && editItem && (
        <Popup
          visible={editPopupVisible}
          dragEnabled={false}
          closeOnOutsideClick={false}
          onHiding={handleEditClose}
          showTitle
          title={translate!(
            editItem.id.startsWith('file_') ? 'Edit file' : 'Edit hyperlink',
            transFormName,
          )}
          width={400}
          height={'auto'}
        >
          <div style={{ padding: '10px' }}>
            {editItem.id.startsWith('file_') ? (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ marginBottom: '5px' }}>
                  {translate!('Description', transFormName)}
                </div>
                <TextBox
                  value={editDescription}
                  onValueChanged={(e) => setEditDescription(e.value)}
                  width={'100%'}
                />
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ marginBottom: '5px' }}>{translate!('Name', transFormName)}</div>
                  <TextBox
                    value={editName}
                    onValueChanged={(e) => setEditName(e.value)}
                    width={'100%'}
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ marginBottom: '5px' }}>{translate!('URL', transFormName)}</div>
                  <TextBox
                    value={editUri}
                    onValueChanged={(e) => setEditUri(e.value)}
                    width={'100%'}
                  />
                </div>
              </>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginTop: '10px',
              }}
            >
              <Button text={translate!('Cancel', '')} onClick={handleEditClose} />
              <Button text={translate!('Save', '')} type={'success'} onClick={handleEditSave} />
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
}
